import { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/stores/RootStore';
import { authApi } from '@/services/authApi';
import { PRODUCTS_CATALOG, type ProductDomain } from '@/data/mockData';
import SubPageHero from '@/components/SubPageHero';
import ConfirmModal from '@/components/ui/ConfirmModal';
import s from './As.module.scss';

/**
 * [2026-09-12 / 2026-09-13 재설계] A/S 신청 — 정식회원(전화인증 ACTIVE)만([evs-member-access-policy]).
 *
 * [2026-09-13 사용자 확정]
 *   · 분야(안전/환경) 선택 단계 제거 — 고객이 안전/환경 구분을 모를 수 있음(부담). 대신 제품에서 도메인 자동 판별.
 *   · 제품 = EVS 취급 전 제품(PRODUCTS_CATALOG)을 ★커스텀 드롭다운(썸네일 + 안전/환경 그룹 헤더)으로. 자유입력 없음(강제).
 *     (네이티브 <select>는 이미지 불가 → 커스텀 리스트박스. 바깥 클릭·ESC로 닫힘.)
 *   · 회사명·주소 필수 / 구매처·설치일 선택 / 모델명 선택. 도메인은 제출 시 선택 제품에서 파생.
 *   변경 금지 트리거: FormData 필드명은 서버 @RequestParam(AsController.create)와 정확히 일치(domain/productName/…).
 *   연관: AsController.create, AsRepository.insert, MyAs·AdminAs 상세.
 */
const CAT_TO_DOMAIN: Record<ProductDomain, string> = { safety: 'SAFETY', environment: 'ENV' };
const DOMAIN_GROUPS: { key: ProductDomain; label: string }[] = [
	{ key: 'safety', label: '안전' },
	{ key: 'environment', label: '환경' },
];

function AsForm({ embedded }: { embedded?: boolean }) {
	const auth = useAuth();
	const nav = useNavigate();
	const [productId, setProductId] = useState('');
	const [pickOpen, setPickOpen] = useState(false);
	const [company, setCompany] = useState('');
	const [address, setAddress] = useState('');
	const [purchase, setPurchase] = useState('');
	const [installDate, setInstallDate] = useState('');
	const [model, setModel] = useState('');
	const [symptom, setSymptom] = useState('');
	const [file, setFile] = useState<File | null>(null);
	const [fe, setFe] = useState<Record<string, string>>({});
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState<string | null>(null);
	const [doneId, setDoneId] = useState<number | null>(null);
	const formRef = useRef<HTMLDivElement>(null);

	if (auth.ready && !auth.isAuthed) return <Navigate to="/login" replace />;

	const selProd = PRODUCTS_CATALOG.find((p) => p.id === productId);
	const domain = selProd ? CAT_TO_DOMAIN[selProd.domain] : '';   // 제품에서 도메인 자동 판별

	async function submit(e: React.FormEvent) {
		e.preventDefault(); setErr(null);
		const fx: Record<string, string> = {};
		if (!selProd) fx.product = '제품을 선택해주세요.';
		if (!company.trim()) fx.company = '회사명을 입력해주세요.';
		if (!address.trim()) fx.address = '주소를 입력해주세요.';
		if (!symptom.trim()) fx.symptom = '증상·문제점을 입력해주세요.';
		setFe(fx);
		if (Object.keys(fx).length) { formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
		setBusy(true);
		const fd = new FormData();
		fd.append('domain', domain); fd.append('productName', selProd!.name);
		fd.append('productModel', model.trim()); fd.append('symptom', symptom.trim());
		fd.append('companyName', company.trim()); fd.append('address', address.trim());
		fd.append('purchaseSource', purchase.trim()); fd.append('installDate', installDate);
		if (file) fd.append('file', file);
		const r = await authApi.asCreate(fd);
		setBusy(false);
		if (!r.success) return setErr(r.message || 'A/S 신청에 실패했습니다.');
		setDoneId(Number(r.id) || 0);
	}

	const optional = <span style={{ color: '#98a1ac', fontWeight: 400 }}>(선택)</span>;

	function pick(id: string) { setProductId(id); setPickOpen(false); setFe((f) => ({ ...f, product: '' })); }

	return (
		<>
			{!embedded && <SubPageHero title="A/S 신청" subtitle="사후관리 · 접수부터 완료까지 진행상황을 추적합니다"
				image="/images/auth/member-hero.jpg" crumbs={[{ label: 'HOME' }, { label: '고객지원' }, { label: 'A/S 신청' }]} />}
			<div className={s.wrap} ref={formRef}>
				<p className={s.eyebrow}>A/S REQUEST</p>
				<h2 className={s.h2}>A/S <b>신청</b></h2>
				<p className={s.lead}>제품과 증상을 남겨주시면 접수 후 <b>접수 → 원인분석 → 수리 → 완료</b> 단계로 진행상황을 안내합니다.</p>

				<form className={s.card} onSubmit={submit} style={{ maxWidth: 680 }}>
					{err && <div className={`${s.msg} ${s.msgErr}`}>{err}</div>}

					{/* [2026-09-13] 제품 = 커스텀 썸네일 드롭다운(안전/환경 그룹). 분야 선택 없이 제품만 고르면 도메인 자동. */}
					<div className={s.field}>
						<label className={s.label}>제품<span className={s.req}>*</span></label>
						<div className={s.pdBox}>
							<button type="button" className={`${s.pdToggle} ${pickOpen ? s.pdOpen : ''}`} onClick={() => setPickOpen((o) => !o)}
								aria-haspopup="listbox" aria-expanded={pickOpen}>
								{selProd ? (
									<span className={s.pdSel}>
										<span className={s.pdThumb}>{selProd.image
											? <img src={selProd.image} alt="" />
											: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M3 8l9-4 9 4-9 4-9-4zM3 8v8l9 4 9-4V8" strokeLinecap="round" strokeLinejoin="round" /></svg>}</span>
										<span className={s.pdName}>{selProd.name}</span>
										<span className={`${s.badge} ${selProd.domain === 'safety' ? s.bSafety : s.bEnv}`}>{selProd.domain === 'safety' ? '안전' : '환경'}</span>
									</span>
								) : <span className={s.pdPlaceholder}>제품을 선택하세요</span>}
								<svg className={s.pdCaret} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
							</button>
							{pickOpen && (
								<>
									<div className={s.pdBackdrop} onClick={() => setPickOpen(false)} />
									<div className={s.pdMenu} role="listbox" data-lenis-prevent>
										{DOMAIN_GROUPS.map((g) => (
											<div key={g.key}>
												<div className={s.pdGroup}>{g.label}</div>
												{PRODUCTS_CATALOG.filter((p) => p.domain === g.key).map((p) => (
													<button type="button" key={p.id} role="option" aria-selected={p.id === productId}
														className={`${s.pdItem} ${p.id === productId ? s.pdItemSel : ''}`} onClick={() => pick(p.id)}>
														<span className={s.pdThumb}>{p.image
															? <img src={p.image} alt="" />
															: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M3 8l9-4 9 4-9 4-9-4zM3 8v8l9 4 9-4V8" strokeLinecap="round" strokeLinejoin="round" /></svg>}</span>
														<span className={s.pdName}>{p.name}{p.soon ? ' · 출시예정' : ''}</span>
													</button>
												))}
											</div>
										))}
									</div>
								</>
							)}
						</div>
						{fe.product && <p className={s.hintErr}>{fe.product}</p>}
					</div>

					<div className={s.field}>
						<label className={s.label}>회사명<span className={s.req}>*</span></label>
						<input className={s.input} value={company} onChange={(e) => { setCompany(e.target.value); setFe((f) => ({ ...f, company: '' })); }}
							placeholder="예: (주)이브이에스" maxLength={120} />
						{fe.company && <p className={s.hintErr}>{fe.company}</p>}
					</div>

					<div className={s.field}>
						<label className={s.label}>주소<span className={s.req}>*</span></label>
						<input className={s.input} value={address} onChange={(e) => { setAddress(e.target.value); setFe((f) => ({ ...f, address: '' })); }}
							placeholder="제품이 설치된 현장 주소 (방문·입고 처리에 사용)" maxLength={255} />
						{fe.address && <p className={s.hintErr}>{fe.address}</p>}
					</div>

					<div className={s.field}>
						<label className={s.label}>구매처 {optional}</label>
						<input className={s.input} value={purchase} onChange={(e) => setPurchase(e.target.value)}
							placeholder="구매하신 경로·대리점 (보증 확인에 참고)" maxLength={120} />
					</div>

					<div className={s.field}>
						<label className={s.label}>설치일 {optional}</label>
						<input type="date" className={s.input} value={installDate} onChange={(e) => setInstallDate(e.target.value)} />
					</div>

					<div className={s.field}>
						<label className={s.label}>모델명 {optional}</label>
						<input className={s.input} value={model} onChange={(e) => setModel(e.target.value)}
							placeholder="제품 라벨의 모델명(아는 경우)" maxLength={120} />
					</div>

					<div className={s.field}>
						<label className={s.label}>증상·문제점<span className={s.req}>*</span></label>
						<textarea className={s.textarea} value={symptom} onChange={(e) => { setSymptom(e.target.value); setFe((f) => ({ ...f, symptom: '' })); }}
							placeholder="언제부터, 어떤 증상인지 구체적으로 적어주세요. (예: 측정값이 표시되지 않음)" maxLength={2000} />
						{fe.symptom && <p className={s.hintErr}>{fe.symptom}</p>}
					</div>

					<div className={s.field}>
						<label className={s.label}>사진 첨부 <span style={{ color: '#98a1ac', fontWeight: 400 }}>(선택 · jpg·png·pdf, 5MB 이하)</span></label>
						{!file ? (
							<label className={s.fileBtn}>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
								사진 선택
								<input type="file" accept="image/jpeg,image/png,application/pdf" style={{ display: 'none' }}
									onChange={(e) => setFile(e.target.files?.[0] || null)} />
							</label>
						) : (
							<div className={s.fileChip}>
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M4 16l4.5-6 3 4 2.5-3L20 16M5 4h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" /></svg>
								<span className={s.fileName}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)}MB</span>
								<button type="button" onClick={() => setFile(null)} aria-label="첨부 제거">✕</button>
							</div>
						)}
					</div>

					<button className={s.primary} disabled={busy}>{busy ? '접수 중…' : 'A/S 신청'}</button>
				</form>
			</div>
			<ConfirmModal open={doneId != null} alertMode title="A/S 접수 완료" confirmText="진행상황 보기"
				message={`A/S 신청이 접수되었습니다.${doneId ? `\n접수번호 AS-${String(doneId).padStart(6, '0')}` : ''}\n진행상황은 'A/S 현황'에서 확인할 수 있습니다.`}
				onConfirm={() => nav('/support/my-as', { replace: true })} onCancel={() => {}} />
		</>
	);
}

export default observer(AsForm);
